/** View model for catalogue dataset detail → Data Descriptors modal. */
export interface CatalogueDataDescriptorViewModel {
  name: string;
  label: string;
  parameterType: string;
  dataSchema: string;
  description: string;
  unitCode?: string;
  unitText?: string;
}

const DESCRIPTOR_META_KEYS = new Set([
  '@context',
  'type',
  'dataDescriptorLabel',
  'description',
]);

function stripSchemaPrefix(value: string): string {
  return value.trim().replace(/^(iudx:|adex:)/i, '');
}

function formatParameterType(raw: unknown): string {
  if (Array.isArray(raw) && raw.length > 0) {
    return stripSchemaPrefix(String(raw[0]));
  }
  if (raw != null && raw !== '') {
    return stripSchemaPrefix(String(raw));
  }
  return '';
}

function readDescriptorEntries(
  raw: Record<string, unknown>,
): Array<[string, Record<string, unknown>]> {
  const entries: Array<[string, Record<string, unknown>]> = [];

  for (const [key, value] of Object.entries(raw)) {
    if (DESCRIPTOR_META_KEYS.has(key)) continue;
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
      continue;
    }
    entries.push([key, value as Record<string, unknown>]);
  }

  return entries;
}

/**
 * Normalizes item API `dataDescriptor` (object or legacy array) for catalogue display.
 */
export function parseCatalogueDataDescriptors(
  raw: unknown,
): CatalogueDataDescriptorViewModel[] {
  if (raw == null) return [];

  const rows: CatalogueDataDescriptorViewModel[] = [];

  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const record = item as Record<string, unknown>;
      const name = String(record['attributeName'] ?? record['name'] ?? '').trim();
      if (!name) continue;
      rows.push(mapDescriptorEntry(name, record));
    }
    return rows.filter((row) => row.parameterType !== '—' || row.dataSchema !== '—' || row.description !== '—');
  }

  if (typeof raw === 'object') {
    for (const [name, value] of readDescriptorEntries(raw as Record<string, unknown>)) {
      rows.push(mapDescriptorEntry(name, value));
    }
  }

  return rows.filter(
    (row) =>
      row.parameterType !== '—' ||
      row.dataSchema !== '—' ||
      row.description !== '—' ||
      !!row.unitCode ||
      !!row.unitText,
  );
}

function mapDescriptorEntry(
  name: string,
  value: Record<string, unknown>,
): CatalogueDataDescriptorViewModel {
  const parameterType = formatParameterType(value['type']);
  const dataSchemaRaw = String(value['dataSchema'] ?? value['data_schema'] ?? '').trim();
  const dataSchema = dataSchemaRaw ? stripSchemaPrefix(dataSchemaRaw) : '';
  const label = String(value['label'] ?? name).trim() || name;
  const description = String(value['description'] ?? '').trim();
  const unitCode = String(value['unitCode'] ?? value['unit_code'] ?? '').trim();
  const unitText = String(value['unitText'] ?? value['unit_text'] ?? '').trim();

  return {
    name,
    label,
    parameterType: parameterType || '—',
    dataSchema: dataSchema || '—',
    description: description || '—',
    unitCode: unitCode || undefined,
    unitText: unitText || undefined,
  };
}

export function catalogueDescriptorSubtitle(
  descriptor: CatalogueDataDescriptorViewModel,
): string {
  const parts = [descriptor.parameterType, descriptor.dataSchema].filter(
    (part) => part && part !== '—',
  );
  return parts.join(' · ');
}

export function hasCatalogueDescriptorFieldValue(
  value: string | number | boolean | null | undefined,
): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  const trimmed = String(value).trim();
  return trimmed.length > 0 && trimmed !== '—';
}
